/**
 * BasePersona - Base implementation for personas
 *
 * Provides complete persona functionality:
 * - Tool management (add, remove, enable, disable)
 * - View management (multi-view support with scriptable actions)
 * - Event dispatching
 * - Lifecycle management
 */

import { EventDispatcher } from '../EventDispatcher';
import { Viewer } from '../viewer/viewer';
import { IPersona, IPersonaConfig, IPersonaMode, ITool, IToolConfig, IViewConfig, IViewMode, PersonaEvents, IToolbarElement, IToolElement, ToolbarElementType } from './types';
import { getViewsFromConfig, getDefaultViewId } from './helpers';
import { ViewManager } from './ViewManager';

/**
 * Base persona implementation
 *
 * Manages a collection of tools and provides view configuration.
 * Can be used directly or extended for custom behavior.
 *
 * @example
 * const persona = new BasePersona(viewer, {
 *   name: 'annotation',
 *   priority: 10,
 *   exclusive: true,
 *   defaultViewConfig: createMultiViewConfig([...]),
 *   tools: [measurePointConfig, measureDistanceConfig],
 * });
 */
export class BasePersona extends EventDispatcher implements IPersona {
  /** Viewer instance */
  protected viewer: Viewer;

  /** Configuration */
  readonly config: IPersonaConfig;

  /** Internal enabled state */
  private _enabled = false;

  /** Registered tools */
  readonly tools: Map<string, ITool> = new Map();

  /** View manager for handling view lifecycle */
  readonly viewManager: ViewManager;

  /** Current persona mode ID */
  private currentModeId?: string;

  /** Debug mode flag */
  private debug = false;

  /** Tools that are disabled by default (from config) */
  private defaultDisabledTools: Set<string> = new Set();

  /** Last enabled tool per mode (modeId → toolName) */
  private lastToolPerMode: Map<string, string> = new Map();

  /**
   * The tool id about to be enabled, set during the exclusive-switch in
   * `enableTool` while the outgoing tools are disabled, then cleared. Lets a
   * tool's `onDisable` see who's replacing it (e.g. to hand off state to a
   * sibling). Null outside a switch.
   */
  private _incomingToolId: string | null = null;

  /** The tool id being enabled during the current exclusive switch, if any. */
  protected get incomingToolId(): string | null {
    return this._incomingToolId;
  }

  /** Last active mode ID (persisted across persona enable/disable cycles) */
  private lastModeId?: string;

  /**
   * Constructor
   *
   * @param viewer - Viewer instance
   * @param config - Persona configuration
   * @param viewManager - Shared ViewManager from PersonaManager
   */
  constructor(viewer: Viewer, config: IPersonaConfig, viewManager: ViewManager) {
    super();
    this.viewer = viewer;
    this.config = config;

    // Use shared view manager from PersonaManager
    this.viewManager = viewManager;

    // Enable debug logging via environment or localStorage
    if (typeof window !== 'undefined') {
      this.debug = localStorage.getItem('persona_debug') === 'true';
    }
  }

  // ==================== Basic Properties ====================

  get name(): string {
    return this.config.name;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  get priority(): number {
    return this.config.priority ?? 0;
  }

  // ==================== Lifecycle ====================

  /**
   * Enable the persona
   *
   * - Sets enabled state
   * - Activates first mode
   * - Applies default view mode
   * - Calls onEnable() hook
   * - Dispatches ENABLED event
   */
  enable(): void {
    if (this._enabled) {
      this.warn('Already enabled');
      return;
    }

    this._enabled = true;

    // Activate last mode if available, otherwise first mode
    const modes = this.getAvailableModes();
    if (modes.length > 0) {
      // Try to restore last mode, fallback to first mode
      const targetModeId = this.lastModeId && modes.some((m) => m.id === this.lastModeId) ? this.lastModeId : modes[0].id;
      this.setMode(targetModeId);
    }

    // Initialize available views in ViewManager. The default view is computed
    // first and passed as the fallback target so the availability check and
    // the explicit setView below resolve to a single switch.
    const personaDefaultViewId = getDefaultViewId(this.getViewConfig());
    this.updateAvailableViews(personaDefaultViewId);

    // Apply default view (now includes any default tool's view config)
    const views = this.getAvailableViews();
    if (views.length > 0) {
      this.setView(personaDefaultViewId || views[0].id);
    }

    // Call lifecycle hook
    this.onEnable();

    this.dispatchEvent({
      type: PersonaEvents.ENABLED,
      persona: this,
    });
  }

  /**
   * Disable the persona
   *
   * - Disables all enabled tools first
   * - Exits current view
   * - Clears current mode
   * - Sets disabled state
   * - Calls onDisable() hook
   * - Dispatches DISABLED event
   */
  disable(): void {
    if (!this._enabled) {
      this.warn('Already disabled');
      return;
    }

    // Save current mode before disabling (for restoration on next enable)
    if (this.currentModeId) {
      this.lastModeId = this.currentModeId;
    }

    // Disable all tools first
    const enabledTools = this.getEnabledTools();
    for (const tool of enabledTools) {
      tool.disable();
    }

    this._enabled = false;
    this.currentModeId = undefined;

    this.onDisable();

    this.dispatchEvent({
      type: PersonaEvents.DISABLED,
      persona: this,
    });
  }

  /**
   * Hook: Called after persona is enabled
   * Override in subclasses for custom behavior
   */
  protected onEnable(): void {
    // Mode activation handles tool enabling
    // Subclasses can override
  }

  /**
   * Hook: Called after persona is disabled
   * Override in subclasses for custom behavior
   */
  protected onDisable(): void {
    // Disable all enabled tools
    this.tools.forEach((tool) => {
      if (tool.enabled) {
        tool.disable();
      }
    });
    // Subclasses can override
  }

  // ==================== Mode Management ====================

  /**
   * Get all available modes for this persona
   *
   * @returns Array of persona modes
   */
  getAvailableModes(): IPersonaMode[] {
    return this.config.modes || [];
  }

  /**
   * Get currently active mode
   *
   * @returns Current mode or undefined
   */
  getCurrentMode(): IPersonaMode | undefined {
    if (!this.currentModeId) return undefined;
    return this.config.modes?.find((mode) => mode.id === this.currentModeId);
  }

  /**
   * Extract tool configs from toolbar elements
   * Supports both new toolbarElements format and legacy tools array
   *
   * @param mode - Persona mode
   * @returns Array of tool configurations
   */
  protected extractToolConfigs(mode: IPersonaMode): IToolConfig[] {
    // Prefer new toolbarElements format
    if (mode.toolbarElements && mode.toolbarElements.length > 0) {
      return mode.toolbarElements.filter((element): element is IToolElement => element.type === ToolbarElementType.TOOL).map((element) => element.config);
    }

    // Fallback to legacy tools array
    return mode.tools || [];
  }

  /**
   * Get toolbar elements for current mode
   * Provides access to the complete toolbar configuration including spacers and actions
   *
   * @returns Array of toolbar elements or undefined if no mode active
   */
  getToolbarElements(): IToolbarElement[] | undefined {
    const currentMode = this.getCurrentMode();
    return currentMode?.toolbarElements;
  }

  /**
   * Switch to a different mode
   *
   * - Disables all current tools
   * - Removes current mode's tools
   * - Loads new mode's tools
   * - Enables default tools in new mode
   * - Activates mode's view config if available
   * - Dispatches MODE_CHANGED event
   *
   * @param modeId - Mode identifier
   */
  setMode(modeId: string): void {
    const newMode = this.config.modes?.find((mode) => mode.id === modeId);

    if (!newMode) {
      this.warn(`Mode "${modeId}" not found`);
      return;
    }

    // If already in this mode, do nothing
    if (this.currentModeId === modeId) {
      return;
    }

    // Save last enabled tool for current mode before switching
    if (this.currentModeId) {
      const enabledTools = this.getEnabledTools();
      // Find the first exclusive tool that's enabled (main active tool)
      const currentMode = this.getCurrentMode();
      const currentToolConfigs = currentMode ? this.extractToolConfigs(currentMode) : [];
      const activeExclusiveTool = enabledTools.find((tool) => {
        const config = currentToolConfigs.find((tc) => tc.id === tool.name);
        return config?.exclusive;
      });
      if (activeExclusiveTool) {
        this.lastToolPerMode.set(this.currentModeId, activeExclusiveTool.name);
      }
    }

    // Disable and remove all current tools
    const currentTools = Array.from(this.tools.keys());
    for (const toolName of currentTools) {
      const tool = this.tools.get(toolName);
      if (tool?.enabled) {
        tool.disable();
      }
      this.removeTool(toolName);
    }

    // Update current mode
    this.currentModeId = modeId;

    // Extract tool configs from mode (supports both toolbarElements and legacy tools array)
    const toolConfigs = this.extractToolConfigs(newMode);

    // Register new mode's tools (skip auto-enable, we handle it below)
    for (const toolConfig of toolConfigs) {
      this.addTool(toolConfig, true); // skipAutoEnable = true
    }

    // Enable tools if persona is enabled
    if (this._enabled) {
      // Check if we have a saved last tool for this mode
      const lastToolName = this.lastToolPerMode.get(modeId);
      let restoredLastTool = false;

      if (lastToolName) {
        // Try to restore the last tool if it exists in this mode and is currently available
        const lastToolConfig = toolConfigs.find((tc) => tc.id === lastToolName);
        if (lastToolConfig) {
          // Check if tool is available (subclasses like FloorplanPersona override isToolAvailable)
          const isAvailable = this.isToolAvailable(lastToolName);
          if (isAvailable) {
            const lastTool = this.tools.get(lastToolName);
            if (lastTool) {
              // If the last tool is exclusive, enable it via enableTool to trigger proper event dispatch
              if (lastToolConfig.exclusive) {
                this.enableTool(lastToolName);
                // Only mark as restored if the tool was actually enabled
                restoredLastTool = lastTool.enabled;
              }
            }
          }
          // Note: Don't clear lastToolPerMode here - the selection state may arrive async
          // Clearing is handled by subclasses when selection is explicitly lost
        }
      }

      // Enable default tools (if we didn't restore a last tool, or for non-exclusive defaults)
      for (const toolConfig of toolConfigs) {
        if (toolConfig.defaultEnabled) {
          const tool = this.tools.get(toolConfig.id);
          if (tool && !tool.enabled) {
            // If we restored an exclusive last tool, skip enabling other exclusive defaults
            if (restoredLastTool && toolConfig.exclusive) {
              continue;
            }
            tool.enable();
          }
        }
      }
    }

    // Decide the mode's target view BEFORE publishing the new available views,
    // so the ViewManager's availability fallback can be pointed directly at
    // the intended target instead of racing the explicit switch below with an
    // intermediate switch to views[0].
    //
    // An enabled tool that declares its own view config states explicit intent
    // (e.g. the Design selection tools default to X-Ray): its default view
    // wins on mode entry, and the sticky user view is cleared — it belonged to
    // the previous mode's context. Modes whose active tool declares no view
    // config keep the old rules: sticky user view first, current-if-valid,
    // then mode/persona defaults.
    const availableViews = getViewsFromConfig(this.getViewConfig());
    const enabledToolViewConfig = this.getEnabledTools()
      .map((tool) => tool.getViewConfig?.())
      .find((config): config is IViewConfig => config !== undefined);
    const toolDefaultId = enabledToolViewConfig ? getDefaultViewId(enabledToolViewConfig) : undefined;
    let targetViewId: string | undefined;
    if (toolDefaultId && availableViews.some((v) => v.id === toolDefaultId)) {
      this.viewManager.clearLastUserViewId();
      targetViewId = toolDefaultId;
    } else if (availableViews.length > 0) {
      const preferredId = this.viewManager.getLastUserViewId();
      const preferredAvailable = !!preferredId && availableViews.some((v) => v.id === preferredId);
      const currentView = this.viewManager.getCurrentView();

      if (preferredAvailable && preferredId) {
        if (currentView?.id !== preferredId) targetViewId = preferredId;
      } else {
        if (preferredId && !preferredAvailable) this.viewManager.clearLastUserViewId();
        const currentViewValid = !!currentView && availableViews.some((v) => v.id === currentView.id);
        if (!currentViewValid) {
          const modeDefaultId = newMode.viewConfig ? getDefaultViewId(newMode.viewConfig) || getViewsFromConfig(newMode.viewConfig)[0]?.id : undefined;
          const personaDefaultId = this.config.defaultViewConfig ? getDefaultViewId(this.config.defaultViewConfig) : undefined;
          targetViewId = (modeDefaultId && availableViews.some((v) => v.id === modeDefaultId) ? modeDefaultId : undefined) ?? (personaDefaultId && availableViews.some((v) => v.id === personaDefaultId) ? personaDefaultId : undefined) ?? availableViews[0].id;
        }
      }
    }

    // Update available views after tools are configured; when the current
    // view is no longer available the fallback goes straight to the target.
    this.updateAvailableViews(targetViewId);

    if (targetViewId) {
      this.setView(targetViewId);
    }

    this.dispatchEvent({
      type: PersonaEvents.MODE_CHANGED,
      persona: this,
      modeId,
      mode: newMode,
    });

    // Call lifecycle hook
    this.onModeChanged(modeId, newMode);
  }

  /**
   * Hook: Called after mode is changed
   * Override in subclasses for custom behavior
   *
   * @param _modeId - New mode identifier
   * @param _mode - New mode object
   */
  protected onModeChanged(_modeId: string, _mode: IPersonaMode): void {
    // Subclasses can override
  }

  // ==================== Tool Management ====================

  /**
   * Check if a tool is disabled by default
   *
   * @param toolName - Tool identifier
   * @returns True if tool is disabled by default
   */
  isToolDefaultDisabled(toolName: string): boolean {
    return this.defaultDisabledTools.has(toolName);
  }

  /**
   * Get all tools that are disabled by default
   *
   * @returns Array of tool names
   */
  getDefaultDisabledTools(): string[] {
    return Array.from(this.defaultDisabledTools);
  }

  /**
   * Add a tool to this persona
   *
   * @param config - Tool configuration
   * @param skipAutoEnable - If true, skip auto-enabling defaultEnabled tools (used during mode switching)
   * @returns Created tool instance
   * @throws Error if tool name already exists
   */
  addTool(config: IToolConfig, skipAutoEnable = false): ITool {
    if (this.tools.has(config.id)) {
      throw new Error(`Tool "${config.id}" already exists in persona "${this.name}"`);
    }

    // Create tool instance via factory
    const tool = config.factory(this.viewer, this);

    // Register tool
    this.tools.set(config.id, tool);

    // Track if this tool is disabled by default
    if (config.defaultDisabled) {
      this.defaultDisabledTools.add(config.id);
    }

    // Enable by default if configured (and not marked as defaultDisabled)
    // Skip if skipAutoEnable is true (during mode switching, we handle this separately)
    if (!skipAutoEnable && config.defaultEnabled && !config.defaultDisabled && this._enabled) {
      tool.enable();
    }

    this.dispatchEvent({
      type: PersonaEvents.TOOL_ADDED,
      persona: this,
      tool,
    });

    return tool;
  }

  /**
   * Remove a tool from this persona
   *
   * @param name - Tool identifier
   */
  removeTool(name: string): void {
    const tool = this.tools.get(name);
    if (!tool) {
      this.warn(`Tool "${name}" not found`);
      return;
    }

    // Dispose tool
    tool.dispose();

    // Unregister
    this.tools.delete(name);

    this.dispatchEvent({
      type: PersonaEvents.TOOL_REMOVED,
      persona: this,
      tool,
    });
  }

  /**
   * Get a tool by name
   *
   * @param name - Tool identifier
   * @returns Tool instance or undefined
   */
  getTool(name: string): ITool | undefined {
    return this.tools.get(name);
  }

  /**
   * Enable a specific tool
   *
   * Handles exclusive tool logic - if tool is exclusive,
   * disables all other tools in persona first.
   *
   * @param name - Tool identifier
   */
  enableTool(name: string): void {
    const tool = this.tools.get(name);
    if (!tool) {
      this.warn(`Tool "${name}" not found`);
      return;
    }

    // Check if tool is exclusive by looking in current mode's tools
    const currentMode = this.getCurrentMode();
    const toolConfigs = currentMode ? this.extractToolConfigs(currentMode) : [];
    const toolConfig = toolConfigs.find((t: IToolConfig) => t.id === name);
    if (toolConfig?.exclusive) {
      // Expose the incoming tool to tools being disabled, so a tool can decide
      // to hand state off to its sibling (e.g. door/window magic <-> manual
      // preserving the wall focus) instead of tearing it down.
      this._incomingToolId = name;
      // Disable all other tools
      this.tools.forEach((t) => {
        if (t !== tool && t.enabled) {
          t.disable();
          // Dispatch TOOL_DISABLED event for UI updates
          this.dispatchEvent({
            type: PersonaEvents.TOOL_DISABLED,
            persona: this,
            tool: t,
          });
        }
      });

      // Save this exclusive tool as the last tool for current mode
      if (this.currentModeId) {
        this.lastToolPerMode.set(this.currentModeId, name);
      }
    }

    tool.enable();
    this._incomingToolId = null;

    // Decide the view target BEFORE publishing the new available views, so
    // the ViewManager's availability fallback can be pointed directly at the
    // intended target instead of racing the explicit switch below.
    //
    // Rule: if the current view also exists in the new tool's view list, KEEP
    // it (don't disturb the user's view); otherwise fall back to the TOOL's
    // own default view. We never snap to the persona default here — only the
    // tool's config matters once a tool declares one.
    const toolViewConfig = tool.getViewConfig?.();
    let targetViewId: string | undefined;

    if (toolViewConfig?.views?.length) {
      // Honour the user's last explicitly-clicked view as a sticky preference:
      // if it exists in the new tool, switch to it; if not, forget it and use
      // the tool's default. Never carry over a view the user didn't choose.
      const preferredId = this.viewManager.getLastUserViewId();
      const preferredAvailable = preferredId ? toolViewConfig.views.some((v) => v.id === preferredId) : false;
      const currentView = this.viewManager.getCurrentView();

      if (preferredAvailable && preferredId) {
        if (currentView?.id !== preferredId) targetViewId = preferredId;
      } else {
        if (preferredId && !preferredAvailable) this.viewManager.clearLastUserViewId();
        // Switch to the tool default unless the current view already is it.
        if (toolViewConfig.defaultViewId && currentView?.id !== toolViewConfig.defaultViewId) {
          targetViewId = toolViewConfig.defaultViewId;
        }
      }
    } else if (this.config.defaultViewConfig?.views) {
      const currentView = this.viewManager.getCurrentView();
      // Tool declares no view config — fall back to the persona's default views,
      // preserving the current view if it's among them.
      const personaViewIds = this.config.defaultViewConfig.views.map((v) => v.id);
      const isCurrentViewInPersonaDefaults = currentView && personaViewIds.includes(currentView.id);

      if (!isCurrentViewInPersonaDefaults && this.config.defaultViewConfig.defaultViewId) {
        targetViewId = this.config.defaultViewConfig.defaultViewId;
      }
    }

    // Update available views since tool configs may have changed; when the
    // current view drops out of the list, fall back straight to the target.
    this.updateAvailableViews(targetViewId);

    if (targetViewId) {
      this.setView(targetViewId);
    }

    this.dispatchEvent({
      type: PersonaEvents.TOOL_ENABLED,
      persona: this,
      tool,
    });
  }

  /**
   * Disable a specific tool
   *
   * @param name - Tool identifier
   */
  disableTool(name: string): void {
    const tool = this.tools.get(name);
    if (!tool) {
      this.warn(`Tool "${name}" not found`);
      return;
    }

    tool.disable();

    // Update available views since tool configs may have changed
    this.updateAvailableViews();

    this.dispatchEvent({
      type: PersonaEvents.TOOL_DISABLED,
      persona: this,
      tool,
    });
  }

  /**
   * Get all currently enabled tools
   *
   * @returns Array of enabled tools
   */
  getEnabledTools(): ITool[] {
    return Array.from(this.tools.values()).filter((tool) => tool.enabled);
  }

  /**
   * Check if a tool is available (not disabled due to missing requirements)
   * Subclasses can override to implement custom availability logic (e.g., FloorplanPersona checks floor selection)
   *
   * @param _toolName - Tool identifier
   * @returns True if tool is available, false otherwise
   */
  isToolAvailable(_toolName: string): boolean {
    return true;
  }

  /**
   * Get the default tool for the current mode
   *
   * Finds the tool marked with `defaultEnabled: true` in the current mode's config.
   *
   * @returns Default tool instance or undefined
   */
  getDefaultTool(): ITool | undefined {
    const currentMode = this.getCurrentMode();
    if (!currentMode) return undefined;
    const toolConfigs = this.extractToolConfigs(currentMode);
    const defaultConfig = toolConfigs.find((tc) => tc.defaultEnabled);
    if (!defaultConfig) return undefined;
    return this.tools.get(defaultConfig.id);
  }

  /**
   * Reset to the default tool for the current mode
   *
   * Enables the default tool, which (if exclusive) automatically
   * disables all other tools via `enableTool`.
   */
  resetToDefaultTool(): void {
    const defaultTool = this.getDefaultTool();
    if (defaultTool) {
      this.enableTool(defaultTool.name);
    }
  }

  /**
   * Try to restore the last active tool for the current mode.
   * Called during mode switching to restore the previously active tool.
   * Note: This is only used during setMode, NOT when conditions change later
   * (e.g., floor/surface becomes selected after mode switch).
   *
   * @returns True if a tool was restored, false otherwise
   */
  protected tryRestoreLastTool(): boolean {
    if (!this.currentModeId) return false;

    const lastToolName = this.lastToolPerMode.get(this.currentModeId);
    if (!lastToolName) return false;

    // Check if tool is available
    if (!this.isToolAvailable(lastToolName)) return false;

    // Check if tool exists
    const tool = this.tools.get(lastToolName);
    if (!tool) return false;

    // Enable the tool
    this.enableTool(lastToolName);
    return tool.enabled;
  }

  // ==================== View Management ====================

  /**
   * Get merged view configuration
   *
   * If an enabled tool has a view config, return ONLY that tool's config (complete override).
   * Otherwise, return persona's default config.
   *
   * @returns View config (tool override or persona default)
   */
  getViewConfig(): IViewConfig {
    // Get enabled tools' configs
    const enabledTools = this.getEnabledTools();
    const toolConfigs = enabledTools.map((tool) => tool.getViewConfig?.()).filter((config): config is IViewConfig => config !== undefined);

    // If a tool has a view config, use it exclusively (don't merge with persona views)
    if (toolConfigs.length > 0) {
      return toolConfigs[0];
    }

    // No tool config, return persona's default config
    return this.config.defaultViewConfig || {};
  }

  /**
   * Get all available view modes
   *
   * Merges persona views with enabled tool views.
   * Tool views with same ID override persona views.
   *
   * @returns Array of available view modes
   */
  getAvailableViews(): IViewMode[] {
    return this.viewManager.getAvailableViews();
  }

  /**
   * Update available views based on current config
   * Called when tools are enabled/disabled to refresh ViewManager
   */
  private updateAvailableViews(preferredViewId?: string): void {
    const config = this.getViewConfig();
    const views = getViewsFromConfig(config);
    // Async availability fallback runs through the ViewManager's coalescing
    // switch chain; passing the caller's intended target prevents a wasted
    // intermediate switch to views[0] when the current view drops out.
    void this.viewManager.setAvailableViews(views, { preferredViewId });
  }

  /**
   * Get currently active view mode
   *
   * @returns Current view mode or undefined
   */
  getCurrentView(): IViewMode | undefined {
    // Use ViewManager as the source of truth for current view
    return this.viewManager.getCurrentView() || undefined;
  }

  /**
   * Switch to a different view mode
   *
   * - Validates view exists
   * - Calls current view's onExit()
   * - Updates current view ID
   * - Calls new view's onEnter()
   * - Applies view mode settings
   * - ViewManager dispatches VIEW_CHANGED event
   *
   * @param viewId - View mode identifier
   */
  setView(viewId: string, viewContext?: IViewMode['viewContext']): void {
    // Use ViewManager to handle the view switch with optional context.
    // Switches are queued/async; ViewManager dispatches VIEW_CHANGED.
    void this.viewManager.switchToView(viewId, viewContext).then((outcome) => {
      if (outcome === 'not-found') this.warn(`View "${viewId}" not found`);
    });
  }

  /**
   * Check if the current view can be flipped
   * Returns true for plane-specific or line-specific 2D views
   */
  canFlip(): boolean {
    return this.viewManager.canFlip();
  }

  /**
   * Flip the current 2D view (plane or line)
   * Inverts the plane normal or swaps line points, then refreshes the view
   */
  flipView(): void {
    this.viewManager.flipView();
  }

  /**
   * Refresh available views based on current tool state
   * Should be called when a tool's view configuration changes dynamically
   */
  refreshAvailableViews(): void {
    this.updateAvailableViews();
  }

  // ==================== Cleanup ====================

  /**
   * Cleanup and dispose resources
   *
   * - Disables persona if enabled
   * - Disposes all tools
   * - Clears state
   * - Removes event listeners
   */
  dispose(): void {
    // Disable if enabled
    if (this._enabled) {
      this.disable();
    }

    // Dispose all tools
    this.tools.forEach((tool) => tool.dispose());
    this.tools.clear();

    // Remove all event listeners
    this.removeEventListeners(PersonaEvents.ENABLED);
    this.removeEventListeners(PersonaEvents.DISABLED);
    this.removeEventListeners(PersonaEvents.TOOL_ADDED);
    this.removeEventListeners(PersonaEvents.TOOL_REMOVED);
    this.removeEventListeners(PersonaEvents.TOOL_ENABLED);
    this.removeEventListeners(PersonaEvents.TOOL_DISABLED);
  }

  // ==================== Helper Methods ====================

  /**
   * Helper: Log debug message
   */
  protected log(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[Persona:${this.name}] ${message}`, ...args);
    }
  }

  /**
   * Helper: Log warning message
   */
  protected warn(message: string, ...args: unknown[]): void {
    console.warn(`[Persona:${this.name}] ${message}`, ...args);
  }

  /**
   * Helper: Log error message
   */
  protected error(message: string, ...args: unknown[]): void {
    console.error(`[Persona:${this.name}] ${message}`, ...args);
  }
}
